import React, { useState, useEffect, type ComponentType } from "react";
import { Link } from "react-router-dom";
import { trackGA4Event } from "@/lib/analytics";
import { getPost, type BlogMeta } from "@/lib/blog";
import allPosts from "virtual:blog-manifest";
import { PageMeta } from "@/components/PageMeta";
import { JsonLd } from "@/components/JsonLd";
import { canonicalUrl } from "@/lib/canonical";
import FieldHeader from "@/components/field/FieldHeader";
import FieldFooter from "@/components/field/FieldFooter";
import "@/styles/design-tokens.css";
import "../blog.css";

const voiceLabels: Record<string, string> = {
  technical: "Technical",
  "ai-expert": "AI & ML",
  legal: "Compliance",
  marketing: "Product",
};
const voiceLabel = (v: string) => voiceLabels[v] || v;

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

function ShareRow({ title, slug }: { title: string; slug: string }) {
  const [copied, setCopied] = useState(false);
  const url = `https://fhirfly.io/blog/${slug}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); } catch { /* clipboard unavailable */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const t = encodeURIComponent(title);
  const u = encodeURIComponent(url);
  return (
    <div className="share">
      <span className="lbl">Share</span>
      <a href={`https://x.com/intent/tweet?text=${t}&url=${u}`} target="_blank" rel="noopener noreferrer" aria-label="Share on X"><i className="fa-brands fa-x-twitter" /></a>
      <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${u}`} target="_blank" rel="noopener noreferrer" aria-label="Share on LinkedIn"><i className="fa-brands fa-linkedin-in" /></a>
      <button onClick={copy} aria-label="Copy link" title={copied ? "Copied!" : "Copy link"}><i className={copied ? "fa-solid fa-check" : "fa-solid fa-link"} /></button>
      <a href="/rss.xml" target="_blank" rel="noopener noreferrer" aria-label="RSS feed"><i className="fa-solid fa-rss" /></a>
    </div>
  );
}

function RelatedPosts({ currentSlug, currentTags }: { currentSlug: string; currentTags: string[] }) {
  if (currentTags.length === 0) return null;
  const scored = (allPosts as BlogMeta[])
    .filter((p) => p.slug !== currentSlug)
    .map((post) => ({ post, shared: post.tags.filter((t) => currentTags.includes(t)).length }))
    .filter((s) => s.shared > 0)
    .sort((a, b) => b.shared - a.shared)
    .slice(0, 3);
  if (scored.length === 0) return null;
  return (
    <div className="related">
      <h3>Related reading</h3>
      <div className="rgrid">
        {scored.map(({ post }) => (
          <Link key={post.slug} to={`/blog/${post.slug}`}>
            <span className="t">{post.title}</span>
            <span className="d">{formatDate(post.publishedAt)}</span>
          </Link>
        ))}
      </div>
    </div>
  );
}

function BlogNavLinks({ currentSlug }: { currentSlug: string }) {
  const posts = allPosts as BlogMeta[];
  const idx = posts.findIndex((p) => p.slug === currentSlug);
  if (idx === -1) return null;
  const newer = idx > 0 ? posts[idx - 1] : null;
  const older = idx < posts.length - 1 ? posts[idx + 1] : null;
  if (!older && !newer) return null;
  return (
    <nav className="pnav">
      {older ? (
        <Link to={`/blog/${older.slug}`}><span className="dir">← Older</span><span className="t">{older.title}</span></Link>
      ) : <div />}
      {newer ? (
        <Link to={`/blog/${newer.slug}`} style={{ textAlign: "right" }}><span className="dir">Newer →</span><span className="t">{newer.title}</span></Link>
      ) : <div />}
    </nav>
  );
}

export default function BlogPostClient({ slug }: { slug: string }) {
  const [meta, setMeta] = useState<BlogMeta | null>(null);
  const [Content, setContent] = useState<ComponentType | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const postResult = await getPost(slug);
      if (cancelled) return;
      if (!postResult) { setNotFound(true); return; }
      setMeta(postResult.meta);
      setContent(() => postResult.default);
    }
    load();
    return () => { cancelled = true; };
  }, [slug]);

  useEffect(() => {
    if (meta) trackGA4Event("select_content", { content_type: "blog_post", item_id: slug });
  }, [meta, slug]);

  if (notFound) {
    return (
      <div className="ffly ffly--public blog-article">
        <FieldHeader active="blog" />
        <div className="notfound">
          <h1>Post not found</h1>
          <p>The article you're looking for doesn't exist or has moved.</p>
          <Link className="back" to="/blog">← Back to the blog</Link>
        </div>
        <FieldFooter variant="full" />
      </div>
    );
  }

  if (!meta || !Content) {
    return <div className="ffly ffly--public blog-article"><FieldHeader active="blog" /></div>;
  }

  return (
    <div className="ffly ffly--public blog-article">
      <PageMeta
        title={meta.seoTitle || meta.title}
        description={meta.seoDescription || meta.excerpt}
        canonical={canonicalUrl(`/blog/${slug}`)}
        ogType="article"
        ogImage={`https://assets.fhirfly.io/blog/heroes/${slug}.png`}
      />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "Article",
        headline: meta.title,
        description: meta.seoDescription || meta.excerpt,
        image: `https://assets.fhirfly.io/blog/heroes/${slug}.png`,
        datePublished: meta.publishedAt,
        author: { "@type": "Organization", name: "FHIRfly", url: "https://fhirfly.io" },
        publisher: { "@type": "Organization", name: "FHIRfly", logo: { "@type": "ImageObject", url: "https://fhirfly.io/logo.png" } },
        mainEntityOfPage: { "@type": "WebPage", "@id": `https://fhirfly.io/blog/${slug}` },
      }} />

      <FieldHeader active="blog" />

      <article className="art">
        <Link className="back" to="/blog">← All articles</Link>
        <div className="ameta"><span className="tag">{voiceLabel(meta.voice)}</span><span>{formatDate(meta.publishedAt)}</span></div>
        <h1 className="title">{meta.title}</h1>
        <p className="excerpt">{meta.excerpt}</p>
        <ShareRow title={meta.title} slug={slug} />

        <div className="blog-content">
          <Content />
        </div>

        {meta.tags.length > 0 && (
          <div className="tags">
            <span className="k">Tags</span>
            {meta.tags.map((tag) => <span className="chip" key={tag}>{tag}</span>)}
          </div>
        )}

        <BlogNavLinks currentSlug={slug} />
        <RelatedPosts currentSlug={slug} currentTags={meta.tags} />

        <div className="author">
          Written by <strong>The FHIRfly Team</strong> — healthcare data, AI, and interoperability folks building better clinical coding APIs.
        </div>

        <div className="endcta">
          <h3>Build it on real terminology</h3>
          <p>Try any endpoint live — no sign-up required.</p>
          <div className="btns">
            <a className="b1" href="/tools/api-playground">Open the playground →</a>
            <a className="b2" href="/docs">Read the docs</a>
          </div>
        </div>
      </article>

      <FieldFooter variant="full" />
    </div>
  );
}
